import { useFormikContext } from "formik";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { EventFormTranslationValues } from "../../types/event";
import { EventTypeOptionResource } from "../../types/eventType";
import { EventStepKey } from "./EventStepper";

export const emptyTranslation: EventFormTranslationValues = {
	name: "",
	additionalInfo: [{ type: "paragraph", children: [{ text: "" }] }],
};

export function shouldSkipAddressStep(eventType: EventTypeOptionResource | null): boolean {
	return eventType?.addressRequirement === "prohibited";
}

export function shouldSkipUsersStep(eventType: EventTypeOptionResource | null): boolean {
	return eventType?.userResponsibilityRequirements.every(({ requirement }) => requirement === "prohibited") ?? true;
}

export function shouldSkipParticipationStep(eventType: EventTypeOptionResource | null): boolean {
	return (
		eventType?.participationPermissionRequirement === "prohibited" &&
		eventType?.linkToEventRequirement === "prohibited" &&
		eventType?.plannedPersonCountRequirement === "prohibited"
	);
}

export function shouldSkipPlannedCostStep(eventType: EventTypeOptionResource | null): boolean {
	if (!eventType?.costCategoryRequirements?.length) {
		return true;
	}
	return eventType?.costCategoryRequirements.every((c) => c.requirement === "prohibited");
}

export function shouldSkipDetailsStep(eventType: EventTypeOptionResource | null): boolean {
	return (
		eventType?.formatRequirement === "prohibited" &&
		eventType?.eventCategoryRequirement === "prohibited" &&
		eventType?.industryRequirement === "prohibited" &&
		eventType?.targetGroupRequirement === "prohibited" &&
		eventType?.regionRequirement === "prohibited" &&
		eventType?.organizationalUnitRequirement === "prohibited" &&
		eventType?.tagRequirement === "prohibited" &&
		eventType?.preferenceRequirement === "prohibited" &&
		eventType?.isHighlightRequirement === "prohibited" &&
		eventType?.eventGoalRequirement === "prohibited"
	);
}

export const useSubmitEventStep = () => {
	const navigate = useNavigate();
	const { submitForm } = useFormikContext();

	const handleSubmit = useCallback(
		async (eventType: EventTypeOptionResource | null, currentStep: EventStepKey): Promise<void> => {
			switch (currentStep) {
				case "base-data":
					shouldSkipAddressStep(eventType) ? await handleSubmit(eventType, "address") : navigate("../address");
					break;
				case "address":
					shouldSkipUsersStep(eventType) ? await handleSubmit(eventType, "users") : navigate("../users");
					break;
				case "users":
					shouldSkipParticipationStep(eventType)
						? await handleSubmit(eventType, "participation")
						: navigate("../participation");
					break;
				case "participation":
					shouldSkipPlannedCostStep(eventType)
						? await handleSubmit(eventType, "planned-cost")
						: navigate("../planned-cost");
					break;
				case "planned-cost":
					shouldSkipDetailsStep(eventType) ? await submitForm() : navigate("../details");
					break;
			}
		},
		[navigate, submitForm],
	);

	return handleSubmit;
};

export const getPrevLink = (
	eventType: EventTypeOptionResource | null,
	currentStep: "users" | "participation" | "planned-cost" | "details" | "address",
): string => {
	if (!eventType) {
		return "../base-data";
	}

	switch (currentStep) {
		case "details":
			return shouldSkipPlannedCostStep(eventType) ? getPrevLink(eventType, "planned-cost") : "../planned-cost";
		case "planned-cost":
			return shouldSkipParticipationStep(eventType) ? getPrevLink(eventType, "participation") : "../participation";
		case "participation":
			return shouldSkipUsersStep(eventType) ? getPrevLink(eventType, "users") : "../users";
		case "users":
			return shouldSkipAddressStep(eventType) ? getPrevLink(eventType, "address") : "../address";
		case "address":
			return "../base-data";
	}
};
